import React, { useState, useEffect, useRef } from "react";
import MicIcon from "@mui/icons-material/Mic";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import { Spin } from 'antd';

// Utility function to convert Blob to Base64
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

const MicRecorder = ({ getQuestionFromSpeechToText, sendError, selectedLanguage }) => {
  const [isSpeechToTextLoading, setIsSpeechToTextLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBase64, setAudioBase64] = useState("");
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [dataArray, setDataArray] = useState(new Uint8Array(64));
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const canvasRef = useRef(null);
  const [hasMicroPhoneAccess, setHasMicroPhoneAccess] = useState(false);
  const offsetRef = useRef(0);
  const animationFrameRef = useRef(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const selectedLanguageRef = useRef(null);
  const chunks = useRef([]);

  useEffect(() => {
    const checkMicroPhonePermission = async () => {
      try {
        const permissionResult = await navigator.permissions.query({ name: "microphone" });
        setPermissionStatus(permissionResult.state);

        if (permissionResult.state === 'granted') {
          try {
            setHasMicroPhoneAccess(true);
          } catch (error) {
            console.error('Error accessing camera:', error);
            setHasMicroPhoneAccess(false);
          }
        } else if (permissionResult.state === 'denied') {
          setHasMicroPhoneAccess(false);
        } else if (permissionResult.state === 'prompt') {
          setHasMicroPhoneAccess(true);
        }

        permissionResult.onchange = () => {
          setPermissionStatus(permissionResult.state);
          if (permissionResult.state === 'granted') {
            setHasMicroPhoneAccess(true);
          } else {
            setHasMicroPhoneAccess(false);
          }
        };
      } catch (error) {
        console.error('Permission API or getUserMedia is not supported:', error);
      }
    };

    checkMicroPhonePermission();
  }, []);

  useEffect(() => {
    if (permissionStatus == 'denied' || (permissionStatus == 'prompt' && !hasMicroPhoneAccess)) {
      alert("Microphone access is required. Please enable it in your browser or phone settings.");
      return;
    }
  }, [permissionStatus, hasMicroPhoneAccess]);

  useEffect(() => {
    if (isRecording && analyser) {
      const drawWaveform = () => {
        analyser.getByteTimeDomainData(dataArray);

        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasCtx = canvas.getContext("2d");
        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

        // Create a gradient for the waveform
        const gradient = canvasCtx.createLinearGradient(0, 0, canvas.width, 0);
        gradient.addColorStop(0, "#10B981");
        gradient.addColorStop(0.5, "#059669");
        gradient.addColorStop(1, "#047857");

        canvasCtx.fillStyle = gradient;

        const barWidth = canvas.width / dataArray.length;
        let barHeight;
        let x = offsetRef.current;

        for (let i = 0; i < dataArray.length; i++) {
          const normalizedValue = dataArray[i] / 255;
          const amplifiedValue = Math.pow(normalizedValue, 0.5);
          barHeight = amplifiedValue * canvas.height;

          if (amplifiedValue < 0.62) {
            barHeight = Math.max(4, barHeight * 0.3);
          } else if (amplifiedValue < 0.72) {
            barHeight = Math.max(8, barHeight * 0.6);
          }

          // Add rounded rectangles for better visual appeal
          canvasCtx.beginPath();
          canvasCtx.roundRect(
            x,
            (canvas.height - barHeight) / 2,
            barWidth - 2,
            barHeight,
            2
          );
          canvasCtx.fill();

          x += barWidth;
          if (x >= canvas.width) x -= canvas.width;
        }

        offsetRef.current = (offsetRef.current + 2) % canvas.width;
        animationFrameRef.current = requestAnimationFrame(drawWaveform);
      };

      drawWaveform();

      return () => {
        if (audioContext) {
          audioContext.close();
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [isRecording, analyser, dataArray]);

  const handleMicClick = async (recordingStatus) => {
    try {
      selectedLanguageRef.current = selectedLanguage;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyserNode = audioCtx.createAnalyser();
      setHasMicroPhoneAccess(true);
      analyserNode.fftSize = 128;
      setAudioContext(audioCtx);
      setAnalyser(analyserNode);
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyserNode);
      mediaStream.current = stream;
      mediaRecorder.current = new MediaRecorder(stream);

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        const recordedBlob = new Blob(chunks.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(recordedBlob);
        const base64String = await blobToBase64(recordedBlob);
        handleSpeechToText(base64String);
        console.log(base64String, "base64 audio data");
        setRecordedUrl(url);
        chunks.current = [];
      };

      mediaRecorder.current.onpause = async () => {
        chunks.current = [];
      };

      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (error) {
      setHasMicroPhoneAccess(false);
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      setIsSpeechToTextLoading(true);
      setIsRecording(false);
      mediaRecorder.current.stop();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      setIsRecording(false);
      mediaRecorder.current.pause();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };

  const handleSpeechToText = async (base64Audio) => {
    setIsSpeechToTextLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_CARESAATHIURL}translation/speech-to-text`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            src_lang: selectedLanguageRef.current,
            audio_file: base64Audio
          }),
        }
      );
      if (!res.ok) {
        throw new Error(`Transcription failed (${res.status})`);
      }
      const data = await res.json();
  
      // handle the two possible shapes:
      let transcript;
      if (Array.isArray(data)) {
        // second element is already parsed object
        const obj = typeof data[1] === "string" ? JSON.parse(data[1]) : data[1];
        transcript = obj.display_text || obj.text;
      } else if (data.responseList) {
        transcript =
          data.responseList[0]?.display_text ||
          data.responseList[0]?.outString;
      }
  
      if (transcript) {
        getQuestionFromSpeechToText(transcript);
      } else {
        throw new Error("No transcript in response");
      }
    } catch (err) {
      console.error("STT error:", err);
      sendError(err.message || "Transcription failed");
    } finally {
      setIsSpeechToTextLoading(false);
    }
  };
  

  return (
    <div className="flex items-center">
      {/* Loading State */}
      {isSpeechToTextLoading && (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 mr-2">
          <Spin size="small" />
        </div>
      )}

      {/* Recording State */}
      {isRecording && !isSpeechToTextLoading && (
        <div className="flex items-center bg-white rounded-full shadow-sm border border-gray-200 mr-2">
          {/* Cancel Button */}
          <button
            onClick={cancelRecording}
            className="flex items-center justify-center w-10 h-10 rounded-l-lg bg-red-50 hover:bg-red-100 transition-colors duration-200 border-r border-gray-200"
          >
            <CloseIcon
              sx={{ fontSize: 16, color: '#DC2626' }}
            />
          </button>

          {/* Waveform Container */}
          <div className="flex items-center justify-center px-3 py-2 bg-gray-50">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-600 mr-1">REC</span>
            </div>
            <canvas
              ref={canvasRef}
              width="60"
              height="20"
              className="rounded"
              style={{ backgroundColor: 'transparent' }}
            />
          </div>

          {/* Done Button */}
          <button
            onClick={stopRecording}
            className="flex items-center justify-center w-10 h-10 rounded-r-lg bg-green-50 hover:bg-green-100 transition-colors duration-200 border-l border-gray-200"
          >
            <DoneIcon
              sx={{ fontSize: 16, color: '#059669' }}
            />
          </button>
        </div>
      )}

      {/* Default Mic Button */}
      {!isRecording && !isSpeechToTextLoading && (
        <button
          className="flex items-center justify-center w-10 h-10 rounded-full bg-white border-2 border-teal-600 hover:bg-teal-50 transition-all duration-200 shadow-sm hover:shadow-md mr-2"
          onClick={handleMicClick}
        >
          <MicIcon sx={{ fontSize: 18, color: '#0D9488' }} />
        </button>
      )}
    </div>
  );
};

export default MicRecorder;